/**
 * Auth Storage Utilities
 * Manages saved accounts in localStorage for Fast Sign In feature
 */

export interface SavedAccount {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  lastLoginAt: string;
}

const STORAGE_KEY = "yuklar_saved_accounts";

/**
 * Get all saved accounts from localStorage
 */
export const getSavedAccounts = (): SavedAccount[] => {
  if (typeof window === "undefined") return [];

  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];
    return JSON.parse(data) as SavedAccount[];
  } catch {
    return [];
  }
};

/**
 * Save or update an account in localStorage
 */
export const saveAccount = (
  account: Omit<SavedAccount, "lastLoginAt">,
): void => {
  if (typeof window === "undefined") return;

  const accounts = getSavedAccounts();
  const existingIndex = accounts.findIndex((a) => a.id === account.id);

  const updatedAccount: SavedAccount = {
    ...account,
    lastLoginAt: new Date().toISOString(),
  };

  if (existingIndex >= 0) {
    accounts[existingIndex] = updatedAccount;
  } else {
    accounts.unshift(updatedAccount);
  }

  // Keep only the last 5 accounts
  const trimmedAccounts = accounts.slice(0, 5);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedAccounts));
  } catch {
    // Handle quota exceeded or other storage errors
    console.error("Failed to save account to localStorage");
  }
};

/**
 * Remove an account from localStorage
 */
export const removeAccount = (accountId: string): void => {
  if (typeof window === "undefined") return;

  const accounts = getSavedAccounts();
  const filteredAccounts = accounts.filter((a) => a.id !== accountId);

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredAccounts));
  } catch {
    console.error("Failed to remove account from localStorage");
  }
};

/**
 * Clear all saved accounts
 */
export const clearAllAccounts = (): void => {
  if (typeof window === "undefined") return;

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    console.error("Failed to clear accounts from localStorage");
  }
};
